import { Trans, useTranslation } from "react-i18next";
import { CorePassFailRow, CoreTextField, CoreTextFieldProps } from "../../core";
import i18nPackage from "../../i18n/i18n";
import type { EditConsumerProps, ViewConsumerProps } from "./InstitutionBaseRow";
import InstitutionBaseRow from "./InstitutionBaseRow";
import { InstitutionType, UsageCategory, UsageType } from "@verity/database/prisma";
import { Lms } from "@verity/api-client/types";
import { InputAdornment } from "@mui/material";
import { useInstitutionRowHeader, getDisabledProps } from "./informationHelper";
import { getProctoringMethodName } from "../../editQuiz";
import { usageCategoryToProctoringMethod } from "@verity/proctoring-option";
import styles from "./InstitutionBaseRow.module.css";

function isPending(loading: boolean | undefined, saving: boolean | undefined, ...values: unknown[]) {
	return loading || saving || values.some((value) => value === undefined);
}

type InstitutionTextFieldProps = {
	value: string;
	placeholder?: string;
	onChange?: (value: string) => void;
} & Pick<CoreTextFieldProps, "ariaLabelledBy" | "helperText" | "disabled" | "disabledTooltip" | "InputProps">;

function InstitutionTextField({ value, placeholder, onChange, ...props }: InstitutionTextFieldProps) {
	return (
		<CoreTextField
			{...props}
			value={value}
			placeholder={placeholder}
			onChange={(e) => onChange?.(e.target.value)}
			className={styles.institutionTextField}
		/>
	);
}

type NameHelperTextProps = Pick<EditNameRowProps, "nameInUse" | "loading" | "saving" | "name">;

function NameHelperText({ nameInUse, loading, saving, name }: NameHelperTextProps) {
	const { t } = useTranslation(undefined, { keyPrefix: "institutionSettings.information.name", i18n: i18nPackage });
	if (isPending(loading, saving, name)) return null;
	if (nameInUse) return <CorePassFailRow valid={false} text={t("used")} />;
	if (name === "") return <CorePassFailRow valid={false} text={t("empty")} />;
	return <CorePassFailRow valid text={t("valid")} />;
}

type EditNameRowProps = EditConsumerProps & {
	name?: string;
	onChange: (value: string) => void;
	nameInUse: boolean;
};
type ViewNameRowProps = ViewConsumerProps & {
	name?: string;
};

type InstitutionNameRowProps = EditNameRowProps | ViewNameRowProps;

export function InstitutionNameRow({ name, ...props }: InstitutionNameRowProps) {
	const { t, commonProps } = useInstitutionRowHeader("name", props);

	const value = name ?? "";

	if (props.mode === "edit") {
		return (
			<InstitutionBaseRow
				{...commonProps}
				strategy={{
					mode: "edit",
					required: true,
					createControl: (id) => (
						<InstitutionTextField
							value={name ?? ""}
							placeholder={t("placeholder")}
							onChange={props.onChange}
							helperText={
								<NameHelperText nameInUse={props.nameInUse} loading={props.loading} saving={props.saving} name={name} />
							}
							{...getDisabledProps(props)}
							ariaLabelledBy={id}
						/>
					),
				}}
			/>
		);
	}

	return <InstitutionBaseRow {...commonProps} strategy={{ mode: "view", value, copyAriaLabel: t("copyAriaLabel") }} />;
}

type OriginHelperTextProps = Pick<EditOriginRowProps, "loading" | "saving" | "origin" | "lms" | "isOriginValid">;

function OriginHelperText({ loading, saving, origin, lms, isOriginValid }: OriginHelperTextProps) {
	const { t } = useTranslation(undefined, { keyPrefix: "institutionSettings.information.origin", i18n: i18nPackage });
	if (isPending(loading, saving, origin, lms)) return;

	if (!isOriginValid) return <CorePassFailRow valid={false} text={t("invalid")} />;
	return <CorePassFailRow valid text={t("valid")} />;
}

type EditOriginRowProps = EditConsumerProps & {
	origin?: string;
	onChange: (value: string) => void;
	product: InstitutionType;
	lms?: Lms;
	isOriginValid?: boolean;
};
type ViewOriginRowProps = ViewConsumerProps & {
	origin?: string;
};

type InstitutionOriginRowProps = EditOriginRowProps | ViewOriginRowProps;

export function InstitutionOriginRow({ origin, ...props }: InstitutionOriginRowProps) {
	const { t, commonProps } = useInstitutionRowHeader("origin", props);

	const value = origin ?? "";

	if (props.mode === "edit") {
		const isCandidate = props.product === "candidate";
		const noLms = props.lms === undefined;
		return (
			<InstitutionBaseRow
				{...commonProps}
				strategy={{
					mode: "edit",
					required: !noLms,
					createControl: (id) => (
						<InstitutionTextField
							value={origin ?? ""}
							placeholder={t("placeholder")}
							onChange={props.onChange}
							helperText={
								<OriginHelperText
									lms={props.lms}
									loading={props.loading}
									saving={props.saving}
									origin={origin}
									isOriginValid={props.isOriginValid}
								/>
							}
							{...getDisabledProps(
								props,
								isCandidate || noLms,
								isCandidate ? t("productDisabled") : noLms ? t("platformDisabled") : undefined,
							)}
							ariaLabelledBy={id}
						/>
					),
				}}
			/>
		);
	}

	return (
		<InstitutionBaseRow
			{...commonProps}
			strategy={{ mode: "view", value, placeholder: t("placeholder"), copyAriaLabel: t("copyAriaLabel") }}
		/>
	);
}

type SubdomainHelperTextProps = Pick<
	EditSubdomainRowProps,
	"loading" | "saving" | "subdomain" | "subdomainInUse" | "subdomainValid" | "product"
>;

function SubdomainHelperText({
	loading,
	saving,
	subdomain,
	subdomainInUse,
	product,
	subdomainValid,
}: SubdomainHelperTextProps) {
	const { t } = useTranslation(undefined, {
		keyPrefix: "institutionSettings.information.subdomain",
		i18n: i18nPackage,
	});
	if (isPending(loading, saving, subdomain)) return;
	if (subdomainInUse) return <CorePassFailRow valid={false} text={t("used")} />;
	if (subdomain === "") return <CorePassFailRow valid={false} text={t("empty")} />;
	if (subdomainValid) return <CorePassFailRow valid text={t("valid")} />;
	return (
		<CorePassFailRow
			valid={false}
			text={
				<Trans
					t={t}
					i18nKey={product === "candidate" ? "invalidCandidate" : "invalidVerity"}
					components={{ b: <strong /> }}
				/>
			}
		/>
	);
}

type EditSubdomainRowProps = EditConsumerProps & {
	subdomain?: string;
	onChange: (value: string) => void;
	product: InstitutionType;
	subdomainInUse?: boolean;
	subdomainValid?: boolean;
};
type ViewSubdomainRowProps = ViewConsumerProps & {
	subdomain?: string;
};

type InstitutionSubdomainRowProps = EditSubdomainRowProps | ViewSubdomainRowProps;

export function InstitutionSubdomainRow({ subdomain, ...props }: InstitutionSubdomainRowProps) {
	const { t, commonProps } = useInstitutionRowHeader("subdomain", props);

	const value = subdomain ?? "";

	if (props.mode === "edit") {
		return (
			<InstitutionBaseRow
				{...commonProps}
				strategy={{
					mode: "edit",
					required: true,
					createControl: (id) => (
						<InstitutionTextField
							value={value}
							placeholder={t("placeholder")}
							onChange={props.onChange}
							helperText={
								<SubdomainHelperText
									product={props.product}
									loading={props.loading}
									saving={props.saving}
									subdomain={subdomain}
									subdomainInUse={props.subdomainInUse}
									subdomainValid={props.subdomainValid}
								/>
							}
							{...getDisabledProps(props)}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										{props.product === "candidate" ? ".candidate.yuja.com" : ".verity.yuja.com"}
									</InputAdornment>
								),
							}}
							ariaLabelledBy={id}
						/>
					),
				}}
			/>
		);
	}

	return (
		<InstitutionBaseRow
			{...commonProps}
			strategy={{ mode: "view", value, placeholder: t("placeholder"), copyAriaLabel: t("copyAriaLabel") }}
		/>
	);
}

type EditInstitutionIdRowProps = Omit<EditConsumerProps, "disabled" | "disabledTooltip"> & {
	instituteId?: string;
};
type ViewInstitutionIdRowProps = ViewConsumerProps & {
	instituteId?: string;
};

type InstitutionInstituteIdRowProps = EditInstitutionIdRowProps | ViewInstitutionIdRowProps;

export function InstitutionInstituteIdRow({ instituteId, ...props }: InstitutionInstituteIdRowProps) {
	const { t, commonProps } = useInstitutionRowHeader("institutionId", props);

	const value = instituteId ?? "";

	if (props.mode === "edit") {
		return (
			<InstitutionBaseRow
				{...commonProps}
				strategy={{
					mode: "edit",
					createControl: (id) => (
						<InstitutionTextField
							value={value}
							disabled={true} // Cannot edit the institution ID
							disabledTooltip={t("disabledTooltip")}
							ariaLabelledBy={id}
						/>
					),
				}}
			/>
		);
	}

	return <InstitutionBaseRow {...commonProps} strategy={{ mode: "view", value, copyAriaLabel: t("copyAriaLabel") }} />;
}

type CommonUsageRowProps = {
	usageType: UsageType;
	usageCategory: UsageCategory;
	numUsed?: number;
};

type EditUsageRowProps = Omit<EditConsumerProps, "disabled" | "disabledTooltip"> & CommonUsageRowProps;
type ViewUsageRowProps = ViewConsumerProps & CommonUsageRowProps;

type InstitutionUsageRowProps = EditUsageRowProps | ViewUsageRowProps;

export function InstitutionUsageRow({ usageType, numUsed, ...props }: InstitutionUsageRowProps) {
	const methodName = getProctoringMethodName(usageCategoryToProctoringMethod(props.usageCategory));

	const { t, commonProps } = useInstitutionRowHeader("usage", props, (t) => ({
		label: <Trans t={t} i18nKey={`${usageType}.header`} values={{ methodName }} />,
		description: <Trans t={t} i18nKey={`${usageType}.description`} values={{ methodName }} />,
		hint: <Trans t={t} i18nKey={`${usageType}.hint`} values={{ methodName }} />,
	}));

	const value = numUsed?.toFixed(2) ?? "0";

	if (props.mode === "edit") {
		return (
			<InstitutionBaseRow
				{...commonProps}
				strategy={{
					mode: "edit",
					createControl: (id) => (
						<InstitutionTextField
							value={value}
							disabled={true} // Cannot edit usage count
							disabledTooltip={t("disabledTooltip")}
							ariaLabelledBy={id}
						/>
					),
				}}
			/>
		);
	}

	return (
		<InstitutionBaseRow
			{...commonProps}
			strategy={{ mode: "view", value, copyAriaLabel: t(`${usageType}.copyAriaLabel`) }}
		/>
	);
}
